'use client';

import { MetaDefault } from '@terra/ui-design-system';
import {
  AlertCircleIcon,
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  type TerraIcon,
} from '@terra/ui-icons';
import { token, type Token } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useTargetProfile } from '../../hooks/dashboard/useTargetProfile';
import {
  DASHBOARD_ACTION_LIST_HEIGHT,
  DashboardActionList,
} from './DashboardActionList';
import { DashboardActionRow } from './DashboardActionRow';
import { DashboardCard } from './DashboardCard';
import { countReadyItems, type TargetReadinessState } from './target-readiness';
import { useOpenTargetSettings } from './useOpenTargetSettings';

const READINESS_ICON: Record<TargetReadinessState, TerraIcon> = {
  ready: CheckmarkCircle02Icon,
  attention: AlertCircleIcon,
  missing: CancelCircleIcon,
};

const READINESS_COLOR_TOKEN = {
  ready: 'colors.fg.neutral.success',
  attention: 'colors.fg.neutral.warning',
  missing: 'colors.fg.neutral.error',
} as const satisfies Record<
  TargetReadinessState,
  Extract<Token, `colors.${string}`>
>;

type TargetReadinessCardProps = {
  applicationId: string;
};

export function TargetReadinessCard({
  applicationId,
}: TargetReadinessCardProps) {
  const t = useTranslations('home.readiness');
  const { readiness: items, isLoading } = useTargetProfile(applicationId);
  const { openSettings, openSettingsContextMenu } = useOpenTargetSettings();
  const readyCount = countReadyItems(items);

  if (isLoading || readyCount === items.length) {
    return null;
  }

  return (
    <DashboardCard
      title={t('title')}
      trailingContent={
        <MetaDefault color="fg.neutral.muted">
          {t('progress', { ready: readyCount, total: items.length })}
        </MetaDefault>
      }
      contentHeight={DASHBOARD_ACTION_LIST_HEIGHT}
    >
      <DashboardActionList>
        {items.map((item, index) => {
          const Icon = READINESS_ICON[item.state];

          return (
            <DashboardActionRow
              key={item.key}
              icon={
                <Icon
                  size={16}
                  color={token(READINESS_COLOR_TOKEN[item.state])}
                />
              }
              title={t(`items.${item.key}.title`)}
              description={t(
                `items.${item.key}.${item.messageKey}`,
                item.values,
              )}
              showDivider={index > 0}
              onClick={() => openSettings(item.section)}
              onContextMenu={(event) =>
                openSettingsContextMenu(event, item.section)
              }
            />
          );
        })}
      </DashboardActionList>
    </DashboardCard>
  );
}
